import { useMemo } from "react";

import { skipToken } from "@reduxjs/toolkit/query";

import { newrelic } from "@jti/fe-clients";
import {
  useGetCustomerCartIdQuery,
  useGetShippingMethodsQuery,
  useSetShippingMethodsOnCartMutation,
} from "@jti/store/slices/magentoApiSlice";
import { useTranslations } from "@jti/ui";

export const SET_SHIPPING_METHOD_FIXED_CACHE_KEY = "set-shipping-methods-on-cart-mutation";

export const useDeliveryOptions = () => {
  const { __ } = useTranslations();

  const { data: cartIdData, isLoading: isLoadingCartId } = useGetCustomerCartIdQuery();
  const cartId = cartIdData?.customerCart?.id;

  const { data: shippingMethodsData, isLoading: isLoadingShippingMethods } = useGetShippingMethodsQuery(
    cartId ?? skipToken
  );
  const [setShippingMethod, setShippingMethodMutationState] = useSetShippingMethodsOnCartMutation({
    fixedCacheKey: SET_SHIPPING_METHOD_FIXED_CACHE_KEY,
  });

  const allDeliveryOptions = useMemo(() => shippingMethodsData?.methods ?? [], [shippingMethodsData?.methods]);

  const setShippingMethodOnCart = async (methodCode: string) => {
    const selected = allDeliveryOptions.find((option) => option.methodCode === methodCode);

    if (!cartId || !selected?.carrierCode) {
      const errorMessage = __("cart.checkout.error.cannot_set_shipping_method", [cartId ?? "undefined", methodCode]);
      const error = new Error(errorMessage);

      newrelic.noticeError(error);
      throw error;
    }

    return setShippingMethod({
      cartId,
      carrierCode: selected.carrierCode,
      methodCode,
    });
  };

  const isLoading = isLoadingCartId || isLoadingShippingMethods;

  return {
    cartId,
    deliveryOptionsData: shippingMethodsData,
    allDeliveryOptions,
    setShippingMethodOnCart,
    setShippingMethodMutationState,
    isLoading,
  };
};
